/**
 * WHICH ANIMATIONS ARE WORTH WAITING FOR, AND HOW TO WAIT FOR THEM.
 *
 * Two hooks in this layer keep something on screen until its animation has finished -
 * `useMotion` holds a surface for its exit, `useLoadingBlend` holds the blend class for
 * its fade - and both used to answer "how long" with a NUMBER copied from a token. Both
 * now answer it with the browser's own list, and the rule for reading that list lives
 * here so the two cannot drift apart.
 *
 * THE LIST IS NOT A PROMISE THAT ANYTHING WILL END. `element.getAnimations()` returns
 * every animation the browser associates with the element, and several kinds of entry in it
 * never settle:
 *
 *  - an INFINITE one (`animation-iteration-count: infinite`, which is what the skeleton's
 *    pulse and the spinner are). Its `finished` promise is created and never resolved.
 *  - a PAUSED one, and one that is merely `idle` because its animation has not been
 *    started yet or was already committed and removed.
 *  - a running animation with a zero or nonfinite playback rate.
 *  - one with no effect or no end time at all.
 *
 * Waiting on any of those means the element stays in the tree for the rest of the
 * session, which is the failure this whole construction exists to avoid. So an animation
 * counts only when its effect is FINITE - a finite iteration count and a finite end time -
 * and it is `running` or already `finished`. Everything else is treated as absent, which
 * puts the caller on the same next-frame path it takes when there is no animation at all.
 *
 * `allSettled`, not `all`: a cancelled animation REJECTS, and an interrupted exit that
 * leaves a surface mounted forever is worse than one that leaves a frame early.
 */

/** The animations a caller may wait on: finite, and actually going somewhere. */
export function settleable(animations: readonly Animation[]): readonly Animation[] {
  return animations.filter((animation) => {
    if (animation.playState !== 'running' && animation.playState !== 'finished') return false;
    if (
      animation.playState === 'running' &&
      (!Number.isFinite(animation.playbackRate) || animation.playbackRate === 0)
    ) {
      return false;
    }
    const effect = animation.effect;
    if (effect === null || effect === undefined) return false;
    const timing = effect.getComputedTiming();
    return (
      typeof timing.iterations === 'number' &&
      Number.isFinite(timing.iterations) &&
      typeof timing.endTime === 'number' &&
      Number.isFinite(timing.endTime)
    );
  });
}

/** Every animation the element is running that is worth waiting for. */
export function settleableOn(element: Element | null): readonly Animation[] {
  if (element === null || typeof element.getAnimations !== 'function') return [];
  return settleable(element.getAnimations());
}

/**
 * Resolves when every one of them has settled, however it settled. The caller decides what
 * to do with an empty list; this function does not, because "wait for nothing" and "wait
 * for a frame" are different answers and only the caller knows which one it means.
 */
export async function whenSettled(animations: readonly Animation[]): Promise<void> {
  await Promise.allSettled(animations.map((animation) => animation.finished));
}
